import { useState, useMemo, useEffect } from 'react';

import ApiClient from '@lib/apiClient';

import CommonImage from '@/components/CommonImage';
import CreateAndEditDashboard from '@/features/task-configuration/components/CreateAndEditDashboard';
import DashboardCard from '@/features/task-configuration/components/DashboardCard';
import DeleteConfirmationModal from '@/components/DeleteConfirmationModal';
import {
	DASHBOARD_TAB_ITEMS,
	DASHBOARD_SECTIONS,
	DASHBOARD_CONFIG_TAB,
	DASHBOARD_CONFIG_TAB_ITEMS
} from '@/features/task-configuration/constants';
import { useGetUserByAllTypeQuery, useGetUserLevelListQuery } from '@/api/commonApis';
import { printApiError, replaceUnderLine } from '@/utils/Util';
import {
	useGetDashboardCardByUserTypeQuery,
	useRemoveTaskDashboardCardMutation,
	useRemoveTaskDashboardFilterMutation
} from '@/features/task-configuration/api/taskConfigurationApi';
import { toastError, toastSuccess } from '@/components/Toast';
import DashboardSectionTab from '@/features/task-configuration/components/DashboardSectionTab';
import NoData from '@/components/NoData';
import LoaderComponent from '@/components/LoaderComponent';

const DashboardConfiguration = () => {
	const [searchKey, setSearchKey] = useState('');
	const [isExpanded, setIsExpanded] = useState(true);
	const [isOpenCreateDashboardModal, setIsOpenCreateDashboardModal] = useState(false);
	const [selectedCardToEdit, setSelectedCardToEdit] = useState(null);
	const [taskActiveTab, setTaskActiveTab] = useState(DASHBOARD_TAB_ITEMS.OTHERS_PENDING_TASK);
	const [cardToDelete, setCardToDelete] = useState(null);
	const [configTab, setConfigTab] = useState(DASHBOARD_CONFIG_TAB.ROLE);
	const { data: userTypeList = [], isLoading: usersLoading } = useGetUserByAllTypeQuery();
	const { data: roleList = [], isLoading: rolesLoading } = useGetUserLevelListQuery();
	const [selectedUserType, setSelectedUserType] = useState('');
	const [roleSearch, setRoleSearch] = useState('');

	const sidebarOptions = useMemo(() => {
		if (configTab === DASHBOARD_CONFIG_TAB.ROLE) {
			return (roleList || [])
				.filter((role) => role?.id != null && role?.name)
				.map((role) => ({ value: role.id, label: role.name }))
				.filter((option) =>
					roleSearch ? option.label.toLowerCase().includes(roleSearch.toLowerCase()) : true
				)
				.sort((a, b) => a.label.localeCompare(b.label));
		}
		return (userTypeList || [])
			.filter((item) => item !== 'ADMIN')
			.filter((item) => (roleSearch ? item.toLowerCase().includes(roleSearch.toLowerCase()) : true))
			.sort((a, b) => a.localeCompare(b))
			.map((item) => ({ value: item, label: replaceUnderLine(item) }));
	}, [configTab, roleList, userTypeList, roleSearch]);

	const selectedLabel = useMemo(() => {
		if (configTab === DASHBOARD_CONFIG_TAB.ROLE) {
			return (roleList || []).find((role) => role?.id === selectedUserType)?.name || '';
		}
		return replaceUnderLine(selectedUserType);
	}, [configTab, roleList, selectedUserType]);

	useEffect(() => {
		if (selectedUserType) return;
		if (configTab === DASHBOARD_CONFIG_TAB.ROLE) {
			const firstRole = (roleList || [])
				.filter((role) => role?.id != null && role?.name)
				.slice()
				.sort((a, b) => a.name.localeCompare(b.name))?.[0];
			if (firstRole) {
				setSelectedUserType(firstRole.id);
			}
		} else {
			const firstUserType = (userTypeList || [])
				.filter((item) => item !== 'ADMIN')
				.slice()
				.sort((a, b) => a.localeCompare(b))?.[0];
			if (firstUserType) {
				setSelectedUserType(firstUserType);
			}
		}
	}, [configTab, roleList, userTypeList, selectedUserType]);

	const handleConfigTabChange = (tab) => {
		if (tab === configTab) return;
		setConfigTab(tab);
		setRoleSearch('');
		setSelectedUserType('');
	};

	const { data: dashboardCardList = [], isLoading: dashboardLoading } = useGetDashboardCardByUserTypeQuery(
		{
			userType: selectedUserType,
			isRoleTab: configTab === DASHBOARD_CONFIG_TAB.ROLE
		},
		{
			skip: !selectedUserType
		}
	);
	const [removeTaskDashboardCard] = useRemoveTaskDashboardCardMutation();
	const [removeTaskDashboardFilter] = useRemoveTaskDashboardFilterMutation();

	// Material task list cards (role-wise / user-type-wise) from external dashboard service
	const [materialTaskList, setMaterialTaskList] = useState([]);

	useEffect(() => {
		if (!selectedUserType) {
			setMaterialTaskList([]);
			return;
		}

		const params =
			configTab === DASHBOARD_CONFIG_TAB.ROLE
				? { roleId: selectedUserType }
				: { userType: selectedUserType };

		ApiClient.GET('getMaterialTaskListByFilter', params)
			.then(({ data }) => {
				const normalizedMaterialTaskList = Array.isArray(data)
					? data
					: Array.isArray(data?.cardResponses)
						? data.cardResponses
						: [];

				setMaterialTaskList(normalizedMaterialTaskList);
			})
			.catch((error) => {
				setMaterialTaskList([]);
				printApiError(error);
			});
	}, [configTab, selectedUserType]);

	// Group dashboard cards by section
	const groupedDashboardCards = useMemo(() => {
		const grouped = {};

		// Initialize groups for all sections
		DASHBOARD_SECTIONS.forEach((section) => {
			grouped[section.id] = {
				sectionInfo: section,
				cards: []
			};
		});

		// Group cards by their section constant
		dashboardCardList.forEach((card) => {
			const sectionConstant = card?.section?.constant;
			if (sectionConstant && grouped[sectionConstant]) {
				grouped[sectionConstant].cards.push(card);
			}
		});

		return grouped;
	}, [dashboardCardList]);

	// Group material task cards by section
	const groupedMaterialTaskCards = useMemo(() => {
		const grouped = {};

		DASHBOARD_SECTIONS.forEach((section) => {
			grouped[section.id] = {
				sectionInfo: section,
				cards: []
			};
		});

		materialTaskList.forEach((card) => {
			const sectionConstant = card?.section?.constant;
			if (sectionConstant && grouped[sectionConstant]) {
				grouped[sectionConstant].cards.push(card);
			}
		});

		return grouped;
	}, [materialTaskList]);

	const matchesSearchKey = (item) =>
		searchKey ? item?.title?.toLowerCase().includes(searchKey.toLowerCase()) : true;

	const myPendingDashboardCards = (groupedDashboardCards['MY_PENDING_TASKS']?.cards || []).filter(
		matchesSearchKey
	);
	const myPendingMaterialCards = (groupedMaterialTaskCards['MY_PENDING_TASKS']?.cards || []).filter(
		matchesSearchKey
	);

	const othersPendingDashboardCards = (groupedDashboardCards['OTHERS_PENDING_TASKS']?.cards || []).filter(
		matchesSearchKey
	);
	const othersPendingMaterialCards = (groupedMaterialTaskCards['OTHERS_PENDING_TASKS']?.cards || []).filter(
		matchesSearchKey
	);

	const completedDashboardCards = (groupedDashboardCards['COMPLETED_TASKS']?.cards || []).filter(
		matchesSearchKey
	);
	const completedMaterialCards = (groupedMaterialTaskCards['COMPLETED_TASKS']?.cards || []).filter(
		matchesSearchKey
	);

	const myPendingCount =
		(groupedDashboardCards['MY_PENDING_TASKS']?.cards?.length || 0) +
		(groupedMaterialTaskCards['MY_PENDING_TASKS']?.cards?.length || 0);
	const othersPendingCount =
		(groupedDashboardCards['OTHERS_PENDING_TASKS']?.cards?.length || 0) +
		(groupedMaterialTaskCards['OTHERS_PENDING_TASKS']?.cards?.length || 0);
	const completedCount =
		(groupedDashboardCards['COMPLETED_TASKS']?.cards?.length || 0) +
		(groupedMaterialTaskCards['COMPLETED_TASKS']?.cards?.length || 0);

	const handleEditCard = (card) => {
		setSelectedCardToEdit(card);
		setIsOpenCreateDashboardModal(true);
	};

	const handleDeleteCard = (card) => {
		setCardToDelete(card);
	};

	const handleConfirmDelete = async () => {
		try {
			if (cardToDelete?.filterIds && cardToDelete.filterIds.length > 0) {
				const filterDeletePromises = cardToDelete.filterIds.map((filterId) =>
					removeTaskDashboardFilter({ id: filterId }).unwrap()
				);
				await Promise.all(filterDeletePromises);
			}

			await removeTaskDashboardCard({ id: cardToDelete?.id }).unwrap();
			toastSuccess('Dashboard card and configurations deleted successfully');
			setCardToDelete(null);
		} catch (err) {
			toastError(err?.data?.message || 'Failed to delete dashboard card');
		}
	};

	return (
		<LoaderComponent loading={dashboardLoading || usersLoading || rolesLoading}>
			<div className="body-content pending-task-container runing-task-container bg-white">
				<div className="running-task-header d-flex align-items-center justify-content-between mb-3">
					<p className="semibold-14 gray_dark_02">Task configuration</p>
					<div className="task-action d-flex align-items-center">
						<div className="form-group size32 m-0 mr-1 search-filter filter-with-animation">
							<div className="with-icon right-icon">
								<input
									type="text"
									name="search"
									placeholder="Search"
									value={searchKey}
									onChange={(e) => setSearchKey(e.target.value)}
								/>
								<span className="right-icon">
									<CommonImage src="/icons/search.png" alt="" />
								</span>
							</div>
						</div>
						<div className="add-new-design">
							<button
								className="button text size32 px-0"
								onClick={() => setIsOpenCreateDashboardModal(true)}
							>
								<CommonImage src="/icons/add-round.svg" alt="add" />
								<span className="ml-1 gray_dark_02">Dashboard</span>
							</button>
						</div>
					</div>
				</div>
				<div className="running-task-body d-flex justify-content-between align-items-start">
					{/* Department Table */}
					<div className="department-container">
						<div className="config-tab-switch mb-3">
							<div className="inner-tabs">
								<ul>
									{DASHBOARD_CONFIG_TAB_ITEMS.map((tab) => (
										<li
											key={tab.id}
											className={configTab === tab.id ? 'active' : ''}
											onClick={() => handleConfigTabChange(tab.id)}
										>
											{tab.label}
										</li>
									))}
								</ul>
							</div>
						</div>
						<div className="form-group size32 m-0 mr-1 search-filter filter-with-animation mb-3">
							<div className="with-icon right-icon">
								<input
									type="text"
									name="search"
									placeholder="Search"
									value={roleSearch}
									onChange={(e) => setRoleSearch(e.target.value)}
								/>
								<span className="right-icon">
									<CommonImage src="/icons/search.png" alt="" />
								</span>
							</div>
						</div>
						<div className="running-task-table scroll-y-label">
							<table className="table with-border scroll-x-label mb-0">
								<thead>
									<tr>
										<th>SL</th>
										<th>Dashboard for</th>
									</tr>
								</thead>
								<tbody>
									{sidebarOptions.map((option, index) => (
										<tr
											key={option.value}
											onClick={() => setSelectedUserType(option.value)}
											className={`cursor-pointer ${selectedUserType === option.value ? 'active' : ''}`}
										>
											<td className="sl-column">{index + 1}</td>
											<td>{option.label}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>
					{/* right Section - Main Content Dashboad Cards */}
					<div className="flex-grow-1">
						<div className="pending-task-left-section">
							<div className="pending-task-header bg-white d-flex align-items-center justify-content-between">
								<div className="pending-task-summary">
									<p className="semibold-14 gray_dark_01">
										My Pending Tasks:
										<span className="gray_dark_01 ml-1">({myPendingCount})</span>
									</p>
								</div>
							</div>
							{/* Pending Tasks Sections - Display at Top */}
							<div className="all-tasks scroll-y-label">
								{myPendingCount > 0 ? (
									<div className="task-categories d-grid gap-4 grid-columns-3">
										{myPendingDashboardCards.map((card) => (
											<DashboardCard
												key={card.id}
												card={card}
												onEdit={handleEditCard}
												onDelete={handleDeleteCard}
												isShowCount={false}
												shouldDisableFilter={false}
											/>
										))}
										{myPendingMaterialCards.map((card) => (
											<DashboardCard
												key={card.id}
												card={card}
												isReadOnly
												isShowCount={false}
												shouldDisableFilter={false}
											/>
										))}
									</div>
								) : (
									<NoData
										message={`There is no Pending task card for (${selectedLabel}) at this moment`}
									/>
								)}
							</div>
						</div>
						{/**-------- Tab Wrapper for Other Sections and compleated sections -----------**/}
						<div className="other-tasks-section mt-3">
							<div className="other-tasks-header bg-white">
								<div className="navigation-tab bg-white">
									<ul>
										<li
											className={`${taskActiveTab === DASHBOARD_TAB_ITEMS.OTHERS_PENDING_TASK ? 'active' : ''}`}
											onClick={() =>
												setTaskActiveTab(DASHBOARD_TAB_ITEMS.OTHERS_PENDING_TASK)
											}
										>
											<p>Pending with Others: ({othersPendingCount})</p>
										</li>
										<li
											className={`${taskActiveTab === DASHBOARD_TAB_ITEMS.COMPLETED_TASK ? 'active' : ''}`}
											onClick={() =>
												setTaskActiveTab(DASHBOARD_TAB_ITEMS.COMPLETED_TASK)
											}
										>
											<p>Completed task: ({completedCount})</p>
										</li>
									</ul>
								</div>
							</div>
							{/* Other pending task tab */}
							{taskActiveTab === DASHBOARD_TAB_ITEMS.OTHERS_PENDING_TASK &&
								(othersPendingCount > 0 ? (
									<DashboardSectionTab
										isExpanded={isExpanded}
										setIsExpanded={setIsExpanded}
										cards={othersPendingDashboardCards}
										readOnlyCards={othersPendingMaterialCards}
										handleEditCard={handleEditCard}
										handleDeleteCard={handleDeleteCard}
										isShowCount={false}
										shouldDisableFilter={false}
									/>
								) : (
									<NoData
										message={`There is no Others pending task card for (${selectedLabel}) at this moment`}
									/>
								))}
							{/* Compleated task tab */}
							{taskActiveTab === DASHBOARD_TAB_ITEMS.COMPLETED_TASK &&
								(completedCount > 0 ? (
									<DashboardSectionTab
										isExpanded={isExpanded}
										setIsExpanded={setIsExpanded}
										cards={completedDashboardCards}
										readOnlyCards={completedMaterialCards}
										handleEditCard={handleEditCard}
										handleDeleteCard={handleDeleteCard}
										isShowCount={false}
										shouldDisableFilter={false}
									/>
								) : (
									<NoData
										message={`There is no Completed task card for (${selectedLabel}) at this moment`}
									/>
								))}
						</div>
					</div>
				</div>
			</div>
			{/* Modals */}
			{isOpenCreateDashboardModal && (
				<CreateAndEditDashboard
					onClose={() => {
						setIsOpenCreateDashboardModal(false);
						setSelectedCardToEdit(null);
					}}
					selectedCardToEdit={selectedCardToEdit}
					selectedUserType={selectedUserType}
					isRoleTab={configTab === DASHBOARD_CONFIG_TAB.ROLE}
				/>
			)}
			{cardToDelete && (
				<DeleteConfirmationModal
					onClose={() => setCardToDelete(null)}
					onConfirm={handleConfirmDelete}
					title="Delete Dashboard Card"
					description="Are you sure you want to delete this dashboard card? This will also delete all associated filter configurations."
				/>
			)}
		</LoaderComponent>
	);
};

export default DashboardConfiguration;
